import React from 'react'
import SweetAlert from 'react-bootstrap-sweetalert'
import { bindActionCreators, compose } from 'redux'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import NoData from '../../shared/components/NoData/NoData'
import GridContainer from '../../shared/components/Grid/GridContainer'
import {
	deployChallenge,
	fetchChallenge,
	fetchChallengePipeline,
	updateChallenge,
	uploadMedia
} from "actions/challenge.actions"
import challengeStyle from '../../shared/assets/jss/material-dashboard-pro-react/views/challengeStyle'
import withStyles from '@material-ui/core/styles/withStyles'
import { withIntl } from "@hlcr/ui/Intl"
// core components
import Wizard from '../../components/Wizard/Wizard.jsx'
import GridItem from '../../shared/components/Grid/GridItem.jsx'

import GeneralStep from './ChallengeSteps/GeneralStep.jsx'
import CategoryAndTagsStep from './ChallengeSteps/CategoryAndTagsStep.jsx'
import ChallengeStep3 from './ChallengeSteps/SectionsStep.jsx'
import GoldNuggetStep from './ChallengeSteps/SolutionAndGoldNuggetStep'
import ReviewAndSaveStep from './ChallengeSteps/ReviewAndSaveStep'
import { ChallengeModel } from "shared/models/challenge.model"
import type { SectionModelDto } from '../../shared/models/section.model'
import { SectionModel } from "shared/models/section.model"
import ResourcesStep from './ChallengeSteps/ResourcesStep'
import CircularProgress from '@material-ui/core/CircularProgress/CircularProgress'
import { formatFullDate } from "shared/helper/dateCalc"
import { ChallengeLevelModel } from "shared/models/challengeLevel.model"
import { Redirect } from 'react-router-dom'
import { hasWriteAccess } from "shared/auth/authUtils"
import ShowGitlabStatus from '../../shared/helper/ShowGitlabStatus'
import ChallengeQuestionsStep from "views/Challenge/ChallengeSteps/ChallengeQuestionsStep";

class ChallengeDetail extends React.PureComponent {
	constructor(props) {
		super(props)
		this.state = {
			alert: null,
			show: false,
			redirect: false,
			challenge: props.currentChallenge,
			challengeUuid: props.challengeUuid,
			deployStatus: '',
			pipelineId: 0
		}
	}

	componentDidMount() {
		const { fetchChallenge, challengeUuid } = this.props
		if (challengeUuid) {
			const temporaryChallenge: ChallengeModel = this.restoreChallenge(
				challengeUuid
			)
			if (temporaryChallenge) {
				this.setState({
					alert: (
						<SweetAlert
							warning
							title="Local changes found"
							onConfirm={() => {
								this.loadLocalChanges(temporaryChallenge)
							}}
							onCancel={() => {
								this.hideAlert()
								this.removeLocalBackup(challengeUuid)
								fetchChallenge(challengeUuid)
							}}
							confirmBtnCssClass={
								this.props.classes.button + ' ' + this.props.classes.success
							}
							cancelBtnCssClass={
								this.props.classes.button + ' ' + this.props.classes.danger
							}
							confirmBtnText="Yes, restore!"
							cancelBtnText="No, discard local changes!"
							showCancel
						>
							Do you want to restore your local changes from{' '}
							{formatFullDate(temporaryChallenge.modifyDate)}
						</SweetAlert>
					)
				})
			} else {
				fetchChallenge(challengeUuid)
			}
		}
	}

	restoreChallenge = (challengeUuid): ChallengeModel => {
		const cacheData = localStorage.getItem('challengeData')
		if (cacheData) {
			const cachedChallenge = JSON.parse(cacheData)[challengeUuid]
			return ChallengeModel.fromDto(cachedChallenge)
		}
	}

	backupChallenge = (challenge: ChallengeModel) => {
		if (challenge) {
			challenge.modifyDate = new Date()
			let localCache = JSON.parse(localStorage.getItem('challengeData'))
			localCache = localCache ? localCache : {}
			localCache[challenge.uuid] = challenge.toDto()
			// Disables localStorage for the moment
			//localStorage.setItem('challengeData', JSON.stringify(localCache))
		}
	}

	removeLocalBackup = uuid => {
		let localCache = JSON.parse(localStorage.getItem('challengeData'))
		if (localCache && localCache[uuid]) {
			delete localCache[uuid]
			localStorage.setItem('challengeData', JSON.stringify(localCache))
		}
	}

	loadLocalChanges = (localChallenge: ChallengeModel) => {
		this.setState({ challenge: localChallenge, alert: null })
	}

	hideAlert = () => {
		this.setState({
			alert: null
		})
	}

	mergeWizardSteps = (allStepStates): ChallengeModel => {
		const challenge: ChallengeModel =
			this.state.challenge !== null
				? this.state.challenge
				: this.props.currentChallenge

		if (challenge && allStepStates) {
			// Change according to step states
			challenge.modifyMessage = 'updated challenge' // allStepStates['review'].modifyMessage
			challenge.modifyDate = new Date()

			if (allStepStates['general']) {
				challenge.name = allStepStates['general'].name
				challenge.abstract = allStepStates['general'].abstract
				challenge.isPublic = allStepStates['general'].isPublic
				challenge.level = ChallengeLevelModel.fromDto(
					allStepStates['general'].level
				)
				challenge.type = allStepStates['general'].type
			}

			if (allStepStates['category-and-tags']) {
				challenge.categories = allStepStates['category-and-tags'].categories
				challenge.keywords = allStepStates['category-and-tags'].keywords
			}

			if (allStepStates['goldnugget']) {
				challenge.goldNugget.type = allStepStates['goldnugget'].goldNuggetType
				challenge.goldNugget.secret =
					allStepStates['goldnugget'].goldNuggetSecret
				challenge.grading = allStepStates['goldnugget'].grading
			}

			if (allStepStates['sections']) {
				const sections: SectionModelDto = allStepStates['sections'].sections
				challenge.sections = SectionModel.fromDtoList(sections)
			}

			if (allStepStates['challengeQuestions']) {
				challenge.challengeQuestions = allStepStates['challengeQuestions'].challengeQuestions
			}

			if (allStepStates['resources']) {
				challenge.resources = allStepStates['resources'].resources
			}

			if (allStepStates['review']) {
				challenge.ownerEmail = allStepStates['review'].ownerEmail
			}
		}
		return challenge
	}

	handleOnBlur = allStepStates => {
		const challenge: ChallengeModel = this.mergeWizardSteps(allStepStates)
		this.backupChallenge(challenge)
	}

	save(allStepStates, callback) {
		if (hasWriteAccess(this.props.currentChallenge)) {
			this.setState({
				alert: (
					<LoadingModal
						title="Saving your changes"
						onConfirm={() => this.hideAlert()}
					/>
				)
			})

			const challenge: ChallengeModel = this.mergeWizardSteps(allStepStates)

			this.props.updateChallenge(
				this.props.challengeUuid,
				challenge,
				allStepStates['general'].titleImageFile,
				() => {
					callback()
					this.removeLocalBackup(challenge.uuid)
				},
				() => {
					this.setState({ alert: null })
				}
			)
		} else {
			callback()
		}
	}

	handleFinish = allStepStates => {
		this.save(allStepStates, () =>
			this.setState({ alert: null, redirect: true, challenge: null })
		)
	}

	handleFinishDeploy = allStepStates => {
		this.save(allStepStates, () =>
			this.handleDeployChallenge(this.props.challengeUuid)
		)
	}

	handleDeployChallenge = projectUuid => {
		this.setState({
			alert: (
				<LoadingModal
					title={'Deploying Challenge'}
					onConfirm={() => this.hideAlert()}
				/>
			)
		})
		this.props.deployChallenge(projectUuid, result => {
			if (result && result.id) {
				this.setState({ pipelineId: result.id })
				this.setState({ deployStatus: result.status })
				this.interval = setInterval(
					() => this.handleFetchChallengePipeline(result.id),
					5000
				)
			} else {
				this.setState({
					alert: (
						<DeployModal
							title={'Deploy Challenge Error'}
							classes={this.props.classes}
							onConfirm={() => this.setState({ alert: null, redirect: false })}
							showConfirm={true}
							gitlabStatus={'failed'}
						/>
					)
				})
			}
		})
	}

	handleFetchChallengePipeline = pipelineId => {
		this.props.fetchChallengePipeline(pipelineId, result => {
			if (result && result.status) {
				this.setState({
					deployStatus: result.status,
					alert: (
						<DeployModal
							title={'Deploy Challenge Status'}
							classes={this.props.classes}
							onConfirm={() =>
								this.setState({ alert: null, redirect: true, challenge: null })
							}
							showConfirm={
								result.status.match(/^(success|failed|canceled|skipped)$/) !=
								null
							}
							gitlabStatus={result.status}
						/>
					)
				})
				if (result.status.match(/^(success|failed|canceled|skipped)$/)) {
					clearInterval(this.interval)
				}
			} else {
				this.setState({
					alert: (
						<DeployModal
							title={'Deploy Challenge Error'}
							classes={this.props.classes}
							onConfirm={() => this.setState({ alert: null, redirect: false })}
							showConfirm={true}
							gitlabStatus={'failed'}
						/>
					)
				})
				clearInterval(this.interval)
			}
		})
	}

	render() {
		const { currentChallenge, currentChallengePending } = this.props
		const { challenge, redirect } = this.state

		if (redirect) {
			return (
				<Redirect to={`/challenges/search?q=${this.props.challengeUuid}`} />
			)
		}

		if (currentChallengePending) {
			return (
				<LoadingModal
					title="Loading Challenge"
					onConfirm={() => this.hideAlert()}
				/>
			)
		}

		if (!currentChallenge && !challenge)
			return (
				<div>
					{this.state.alert}
					<NoData />
				</div>
			)

		this.backupChallenge(currentChallenge)

		return (
			<div>
				{this.state.alert}
				<ProjectDetail
					{...this.props}
					currentChallenge={currentChallenge}
					mergeWizardSteps={allStepStates =>
						this.mergeWizardSteps(allStepStates)
					}
					handleFinish={this.handleFinish}
					handleFinishDeploy={this.handleFinishDeploy}
					handleOnBlur={this.handleOnBlur}
				/>
			</div>
		)
	}
}

const LoadingModal = ({ title, onConfirm }) => (
	<SweetAlert title={title} onConfirm={onConfirm} showConfirm={false}>
		<CircularProgress />
	</SweetAlert>
)

const DeployModal = ({
	title,
	classes,
	onConfirm,
	showConfirm,
	gitlabStatus
}) => (
	<SweetAlert
		title={title}
		onConfirm={onConfirm}
		confirmBtnText={'Close'}
		confirmBtnCssClass={classes.button + ' ' + classes.success}
		showConfirm={showConfirm}
	>
		<ShowGitlabStatus status={gitlabStatus} />
	</SweetAlert>
)

const ProjectDetail = ({ ...props }) => {
	return (
		<GridContainer justify="center">
			<GridItem xs={12} sm={10}>
				<Wizard
					color={'primary'}
					validate
					steps={
						hasWriteAccess(props.currentChallenge)
							? [
									{
										stepName: 'General',
										stepComponent: GeneralStep,
										stepId: 'general',
										props: { ...props }
									},
									{
										stepName: 'Category & Tags',
										stepComponent: CategoryAndTagsStep,
										stepId: 'category-and-tags',
										props: { ...props }
									},
									{
										stepName: 'Grading & Flag',
										stepComponent: GoldNuggetStep,
										stepId: 'goldnugget',
										props: { ...props }
									},
									{
										stepName: 'Sections',
										stepComponent: ChallengeStep3,
										stepId: 'sections',
										props: { ...props }
									},
								  {
									  stepName: 'Questions',
									  stepComponent: ChallengeQuestionsStep,
									  stepId: 'challengeQuestions',
									  props: { ...props }
								  },
									{
										stepName: 'Resources',
										stepComponent: ResourcesStep,
										stepId: 'resources',
										props: { ...props }
									},
									{
										stepName: 'Review & Save',
										stepComponent: ReviewAndSaveStep,
										stepId: 'review',
										props: { ...props }
									}
							  ]
							: [
									{
										stepName: 'Review & Save',
										stepComponent: ReviewAndSaveStep,
										stepId: 'review',
										props: { ...props }
									}
							  ]
					}
					title="Challenge Editor"
					subtitle={props.currentChallenge.uuid}
					finishButtonClick={props.handleFinish}
					finishButtonDeployClick={props.handleFinishDeploy}
					handleOnBlur={props.handleOnBlur}
					finishButtonText={
						hasWriteAccess(props.currentChallenge) ? 'Save' : 'Close'
					}
					finishButtonDeployText={
						hasWriteAccess(props.currentChallenge)
							? 'Save & Deploy'
							: 'Close & Deploy'
					}
					initialStepId={props.wizardStep}
					history={props.history}
				/>
			</GridItem>
		</GridContainer>
	)
}

ChallengeDetail.propTypes = {
	classes: PropTypes.object.isRequired,
	challengeUuid: PropTypes.string.isRequired,
	currentChallenge: PropTypes.object,
	currentChallengePending: PropTypes.bool.isRequired,
	wizardStep: PropTypes.string,
	fetchChallenge: PropTypes.func.isRequired,
	uploadMedia: PropTypes.func.isRequired
}

const mapStateToProps = (state, ownProps) => {
	const challengeUuid = ownProps.match.params.challengeUuid
	const wizardStep = ownProps.match.params.wizardStep

	const currentChallenge = state.api.resources.currentChallenge.data.find(
		c => c.uuid === challengeUuid
	)
	return {
		challengeUuid: challengeUuid,
		currentChallenge: ChallengeModel.fromDto(currentChallenge),
		currentChallengePending: state.api.resources.currentChallenge.pending,
		wizardStep: wizardStep
	}
}

const mapDispatchToProps = dispatch =>
	bindActionCreators(
		{
			fetchChallenge,
			deployChallenge,
			fetchChallengePipeline,
			updateChallenge,
			uploadMedia
		},
		dispatch
	)

ChallengeDetail = compose(
	connect(
		mapStateToProps,
		mapDispatchToProps
	),
	withStyles(challengeStyle),
	withIntl
)(ChallengeDetail)

export default ChallengeDetail
